import { BaseApiService, unwrapApiResponse } from './base';

export interface PolicyResponse {
  policyId: string;
  cedarPolicy: string;
  name: string;
  description: string;
  isSystem: boolean;
  createdAt: string;
  createdBy?: string;
}

export interface GenerateResponse {
  cedarPolicy: string;
  explanation: string;
  warnings?: string[];
}

export interface ValidateResponse {
  valid: boolean;
  errors?: string[];
}

export interface TestResponse {
  decision: 'ALLOW' | 'DENY';
  matchedPolicies: string[];
  determiningPolicies?: { policyId: string }[];
  errors?: string[];
}

export interface PolicyHistoryEntry {
  version: number;
  cedarText: string;
  changeType: 'CREATE' | 'UPDATE' | 'DELETE' | 'REVERT';
  changedBy: string;
  changedAt: string;
  description?: string;
  previousCedarText?: string;
  revertedFromVersion?: number;
}

export interface RevertResponse {
  policyId: string;
  cedarPolicy: string;
  description: string;
  revertedFromVersion: number;
  originalPolicyId: string;
}

export interface SimulationImpact {
  role: string;
  groups: string[];
  gained: { action: string; label: string }[];
  lost: { action: string; label: string }[];
  unchanged: number;
}

export interface SimulationResult {
  impacts: SimulationImpact[];
  summary: string;
}

class PolicyApiService extends BaseApiService {
  async listPolicies(): Promise<PolicyResponse[]> {
    const response = await this.get('/admin/policies');
    const data = await response.json();
    const unwrapped = unwrapApiResponse<any>(data);
    // API may return { policies: [...] } or a direct array
    return unwrapped.policies || unwrapped;
  }

  async createPolicy(cedarPolicy: string, name: string, description: string): Promise<PolicyResponse> {
    const response = await this.post('/admin/policies', { cedarPolicy, name, description });
    const data = await response.json();
    return unwrapApiResponse<PolicyResponse>(data);
  }

  async deletePolicy(policyId: string): Promise<void> {
    await this.delete(`/admin/policies/${encodeURIComponent(policyId)}`);
  }

  async generatePolicy(
    prompt: string,
    conversationHistory?: { role: string; content: string }[]
  ): Promise<GenerateResponse> {
    const body: any = { prompt };
    if (conversationHistory && conversationHistory.length > 0) {
      body.messages = conversationHistory;
    }
    const response = await this.post('/admin/policies/generate', body);
    const data = await response.json();
    return unwrapApiResponse<GenerateResponse>(data);
  }

  async validatePolicy(cedarPolicy: string): Promise<ValidateResponse> {
    const response = await this.post('/admin/policies/validate', { cedarPolicy });
    const data = await response.json();
    return unwrapApiResponse<ValidateResponse>(data);
  }

  async testPolicy(role: string, action: string): Promise<TestResponse> {
    const response = await this.post('/admin/policies/test', { role, action });
    const data = await response.json();
    const result = unwrapApiResponse<any>(data);
    // Normalize: API returns determiningPolicies but frontend uses matchedPolicies
    return {
      decision: result.decision || 'DENY',
      matchedPolicies: (result.determiningPolicies || []).map((p: any) => p.policyId || p),
      errors: result.errors || [],
    };
  }

  async getPolicyHistory(policyId: string): Promise<PolicyHistoryEntry[]> {
    const response = await this.get(`/admin/policies/${encodeURIComponent(policyId)}/history`);
    const data = await response.json();
    const unwrapped = unwrapApiResponse<any>(data);
    return unwrapped.history || unwrapped;
  }

  async revertPolicy(policyId: string, version: number): Promise<RevertResponse> {
    const response = await this.post(`/admin/policies/${encodeURIComponent(policyId)}/revert`, { version });
    const data = await response.json();
    return unwrapApiResponse<RevertResponse>(data);
  }

  async simulatePolicy(cedarPolicy: string): Promise<SimulationResult> {
    const response = await this.post('/admin/policies/simulate', { cedarPolicy });
    const data = await response.json();
    return unwrapApiResponse<SimulationResult>(data);
  }
}

export const policyApi = new PolicyApiService();
